import { Button, Form, Input, Skeleton } from 'antd';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { EasyField } from 'react-antd-formutil';
import { useLocation } from 'react-router-dom';
import useDebounceFn from 'app/hooks/useDebounceFn';
import Card, { CardButtonWrap } from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import ScrollTop from 'components/ScrollTop';
import { LongShortEnum, SymbolTypeEnum, TransferAssetFormData } from 'modules/Transfer/type';
import withTransferForm, { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import { PARTIAL_ACATS_BASE_PATH } from '../../helper';
import { PartialDetailsPageFormutil } from '../../type';
import { normalizeTicker, symbolLabelMap, symbolPlaceholderMap, titleMap } from '../helper';
import type { AcceptedFundItem } from '../hooks';
import { useSearchFund } from '../hooks';
import style from '../style.scss';

const FillFund: FC<TransferFormProps<TransferAssetFormData>> = ({
    $formutil,
    getStorageDataByKey,
    onSubmit,
    setStorageDataByKey,
    goNextPath
}) => {
    const location = useLocation();
    const locationState = location.state as { isEdit: boolean; editFundSymbol: string } | undefined;
    const { isEdit, editFundSymbol } = locationState || {};
    const partialDetailsData = getStorageDataByKey<PartialDetailsPageFormutil | undefined>(PageIdEnum.PARTIAL_DETAILS);
    const { temporaryFundList } = partialDetailsData || {};
    const { $params, $setValues } = $formutil;

    const normalizedEditSymbol = editFundSymbol ? normalizeTicker(editFundSymbol) : '';

    const [searchKeyword, setSearchKeyword] = useState('');
    const { data: fundList, isLoading, error: fundListError, mutate: mutateFundList } = useSearchFund(searchKeyword);

    const fundInfoByTicker = useMemo(() => {
        const map = new Map<string, AcceptedFundItem>();

        (fundList || []).forEach(item => {
            map.set(normalizeTicker(item.ticker), item);
        });

        return map;
    }, [fundList]);

    const { run: debouncedSetSearchKeyword } = useDebounceFn((keyword: string) => setSearchKeyword(keyword), {
        wait: 300
    });

    const fundOptions = useMemo(() => {
        if (!fundList || fundList.length === 0) {
            return [];
        }

        return fundList.slice(0, 10).map(item => {
            const ticker = normalizeTicker(item.ticker);
            const disabled =
                (!isEdit || ticker !== normalizedEditSymbol) &&
                temporaryFundList?.some(fund => normalizeTicker(fund.symbol) === ticker);

            return {
                value: ticker,
                label: item.name,
                extraLabel: (
                    <div>
                        <span>{item.name}</span>
                        <div className={style.inputTip}>{ticker}</div>
                    </div>
                ),
                disabled
            };
        });
    }, [fundList, normalizedEditSymbol, isEdit, temporaryFundList]);

    const handleFundSearch = (keyword: string) => {
        debouncedSetSearchKeyword(keyword);
    };

    const handleFundChange = (value: string) => {
        const ticker = normalizeTicker(value);
        const fundInfo = fundInfoByTicker.get(ticker);

        $setValues({
            symbol: ticker,
            temporarySymbol: ticker,
            symbol_name: fundInfo?.name || $params.symbol_name || '',
            cusipNum: fundInfo?.cusipNum ?? $params.cusipNum
        });
    };

    const handleDelete = () => {
        onSubmit(() => {
            setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                ...partialDetailsData,
                temporaryFundList:
                    temporaryFundList?.filter(item => normalizeTicker(item.symbol) !== normalizedEditSymbol) || []
            });

            goNextPath('../partial-details');
        });
    };

    const handleSubmit = () => {
        onSubmit(() => {
            const ticker = normalizeTicker($params.symbol);
            const cusipNum = $params.cusipNum || fundInfoByTicker.get(ticker)?.cusipNum;

            const payload: TransferAssetFormData = {
                ...$params,
                symbol: ticker,
                long_short: LongShortEnum.LONG,
                temporarySymbol: ticker,
                symbol_name: fundInfoByTicker.get(ticker)?.name || $params.symbol_name,
                cusipNum,
                secType: 'MUTUAL_FUND',
                type: SymbolTypeEnum.MUTUAL_FUND
            };

            const updatedList =
                isEdit && temporaryFundList
                    ? temporaryFundList.map(item =>
                          normalizeTicker(item.symbol) === normalizedEditSymbol ? payload : item
                      )
                    : [...(temporaryFundList || []), payload];

            setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                ...partialDetailsData,
                temporaryFundList: updatedList
            });

            goNextPath('../partial-details');
        });
    };

    const symbolChecker = (value: string) => {
        const ticker = normalizeTicker(value);

        if (!ticker) {
            return symbolPlaceholderMap[SymbolTypeEnum.MUTUAL_FUND];
        }

        const isDuplicate =
            (!isEdit || ticker !== normalizedEditSymbol) &&
            temporaryFundList?.some(item => normalizeTicker(item.symbol) === ticker);

        if (isDuplicate) {
            return __('This fund has already been added.');
        }

        // 编辑模式下，当前编辑的 ticker 不需要重新校验是否支持
        if (isEdit && ticker === normalizedEditSymbol) {
            return true;
        }

        if (isLoading) {
            return true;
        }

        const matched = fundInfoByTicker.get(ticker);

        if (!matched) {
            return __('This fund is not currently accepted at TradeUP.');
        }

        return true;
    };

    const isInitialized = useRef(false);

    useEffect(() => {
        if (isInitialized.current) {
            return;
        }

        if (isEdit && temporaryFundList) {
            const target = temporaryFundList.find(item => normalizeTicker(item.symbol) === normalizedEditSymbol);

            if (target) {
                $setValues(target);
                isInitialized.current = true;
            }
        } else {
            $setValues({ long_short: LongShortEnum.LONG, secType: 'MUTUAL_FUND', type: SymbolTypeEnum.MUTUAL_FUND });
            isInitialized.current = true;
        }
    }, [isEdit, normalizedEditSymbol, temporaryFundList, $setValues]);

    return (
        <Card form>
            <ScrollTop />
            <h3 className="transfer-title">{titleMap[SymbolTypeEnum.MUTUAL_FUND]}</h3>
            {fundListError ? (
                <ErrorBox error={fundListError} onClick={() => mutateFundList()} />
            ) : (
                <Form noValidate layout="vertical" onFinish={handleSubmit}>
                    <FormItem
                        required
                        name="temporarySymbol"
                        label={symbolLabelMap[SymbolTypeEnum.MUTUAL_FUND]}
                        extra={__('Enter the fund ticker, for example: CHTRX.')}
                        validMessage={{ required: symbolPlaceholderMap[SymbolTypeEnum.MUTUAL_FUND] }}
                        $onFieldChange={handleFundChange}
                        checker={symbolChecker}>
                        <FieldSelect
                            showSearch
                            optionLabelProp="value"
                            onSearch={handleFundSearch}
                            notFoundContent={
                                isLoading ? (
                                    <Skeleton active title={false} paragraph={{ rows: 3, width: '100%' }} />
                                ) : searchKeyword ? (
                                    __('This fund is not currently accepted at TradeUP.')
                                ) : (
                                    symbolPlaceholderMap[SymbolTypeEnum.MUTUAL_FUND]
                                )
                            }
                            placeholder={symbolPlaceholderMap[SymbolTypeEnum.MUTUAL_FUND]}
                            data={fundOptions}
                        />
                    </FormItem>
                    <EasyField name="symbol_name" type="hidden" />
                    <EasyField name="symbol" type="hidden" />
                    <EasyField name="cusipNum" type="hidden" />
                    <EasyField name="secType" type="hidden" $defaultValue="MUTUAL_FUND" />
                    <EasyField name="type" type="hidden" $defaultValue={SymbolTypeEnum.MUTUAL_FUND} />
                    <FormItem
                        required
                        name="quantity"
                        label={__('Transfer:Quantity')}
                        className={style.numberInputItem}
                        validMessage={{ required: __('Please input quantity') }}
                        getValueFromEvent={({ target }) => target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '')}>
                        <Input placeholder={__('Enter')} autoComplete="off" />
                    </FormItem>
                    <CardButtonWrap className="mt-5">
                        <Button size="large" onClick={isEdit ? handleDelete : () => goNextPath('../partial-details')}>
                            {isEdit ? __('Delete') : __('Back')}
                        </Button>
                        <Button type="primary" size="large" htmlType="submit" disabled={isLoading}>
                            {isEdit ? __('Save') : __('Add List')}
                        </Button>
                    </CardButtonWrap>
                </Form>
            )}
        </Card>
    );
};

export default withTransferForm({
    id: PageIdEnum.FILL_FUND,
    step: 3,
    basePath: PARTIAL_ACATS_BASE_PATH,
    cache: false,
    ensure: [PageIdEnum.BROKER, PageIdEnum.CHECK]
})(FillFund);
